import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Navbar({ activePage, setActivePage }) {
  const { currentUser, logoutUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNav = (page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-slate-900 text-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => handleNav('home')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center font-bold text-xl shadow-inner">
              AR
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight block leading-tight">AuthReg Pro</span>
              <span className="text-xs text-slate-400 font-medium">Authentication & Reg Manager</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <button
              onClick={() => handleNav('home')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activePage === 'home'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Home
            </button>

            <button
              onClick={() => handleNav('register')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                activePage === 'register'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>Registration Page</span>
            </button>

            <button
              onClick={() => handleNav('management')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activePage === 'management'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              Reg Management
            </button>

            {currentUser ? (
              <button
                onClick={() => handleNav('dashboard')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePage === 'dashboard'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                My Dashboard
              </button>
            ) : (
              <button
                onClick={() => handleNav('login')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activePage === 'login'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                Login
              </button>
            )}
          </nav>

          {/* User Status / Auth Actions (Right) */}
          <div className="hidden md:flex items-center space-x-3">
            {currentUser ? (
              <div className="flex items-center space-x-3 bg-slate-800/80 px-3 py-1.5 rounded-full border border-slate-700">
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold text-sm">
                  {currentUser.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="text-xs font-semibold text-slate-200 leading-none">{currentUser.name}</p>
                  <p className="text-[10px] text-indigo-300 mt-0.5">{currentUser.role}</p>
                </div>
                <button
                  onClick={() => {
                    logoutUser();
                    setActivePage('home');
                  }}
                  className="ml-2 text-xs bg-slate-700 hover:bg-rose-600 text-slate-200 hover:text-white px-2.5 py-1 rounded transition-colors"
                  title="Logout"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span className="text-xs bg-slate-800 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700">
                  Guest User
                </span>
                <button
                  onClick={() => handleNav('login')}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-3.5 py-1.5 rounded-lg text-sm font-medium shadow-sm transition-colors"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-800 border-t border-slate-700 px-4 pt-2 pb-4 space-y-2">
          <button
            onClick={() => handleNav('home')}
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
              activePage === 'home' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => handleNav('register')}
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
              activePage === 'register' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Registration Page
          </button>
          <button
            onClick={() => handleNav('management')}
            className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
              activePage === 'management' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
            }`}
          >
            Reg Management
          </button>
          {currentUser ? (
            <>
              <button
                onClick={() => handleNav('dashboard')}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                  activePage === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
                }`}
              >
                My Dashboard ({currentUser.name})
              </button>
              <button
                onClick={() => {
                  logoutUser();
                  setActivePage('home');
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-rose-400 hover:bg-slate-700"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => handleNav('login')}
              className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                activePage === 'login' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              Login / Sign In
            </button>
          )}
        </div>
      )}
    </header>
  );
}
